import React, {
  Component,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useTable, useSortBy, useFilters } from "react-table";
import { useAuth } from "../auth/AuthContext";
import { CopyableId } from "./CopyableId";
import { formatStatusLabel } from "./AccountStatusDropdown.tsx";
import { requireToken, SessionExpiredError } from "../auth/authService";
import { Modal, Button, Form } from "react-bootstrap";
import {
  getFromMCAPI,
  updateMerchantInMCAPI,
  getIcPlusPlusStatus,
  updateIcPlusPlusStatus,
  getStartOfDay,
  updateStartOfDay,
  getMerchantDeletability,
  deleteMerchantInMCAPI,
} from "./merchantConfigurationApi";
import { configs } from "../configurations";
import AccountStatusDropdown from "./AccountStatusDropdown.tsx";
import { AccountStatus } from "../types/AccountStatus.ts";
import { PageHeader } from "./PageHeader";
import { ColumnFilter } from "./ColumnFilter";
import {
  START_OF_DAY_TIMEZONES,
  DEFAULT_TIMEZONE,
  formatStartOfDay,
  formatTime,
  isAllowedTime,
  timeOptionsFor,
} from "./startOfDay";

// Global Search component
// function GlobalFilter({ globalFilter, setGlobalFilter }) {
//   return (
//     <InputGroup className="mb-3">
//       <InputGroup.Text>
//         <i className="bi bi-search"></i>
//       </InputGroup.Text>
//       <Form.Control
//         value={globalFilter || ""}
//         onChange={(e) => setGlobalFilter(e.target.value || undefined)}
//         placeholder="Search all columns..."
//         aria-label="Search"
//       />
//     </InputGroup>
//   );
// }

// Column Filter component
//  The platform account the connected accounts hang off. Hardcoded, and REACT_APP_STRIPE_ENV
//  is read straight off window._env_ rather than through configs() -- the only place in the
//  app that bypasses the config layer. Lifted out of the JSX unchanged so the behaviour is
//  identical; it wants fixing, but that is an env-plumbing decision, not a layout one.
const STRIPE_PLATFORM_ACCOUNT = "acct_1JpLdyGHVLl6W3GN";

function stripeAccountUrl(accountId) {
  const mode =
    window._env_?.REACT_APP_STRIPE_ENV === "production" ? "" : "/test";
  return `https://dashboard.stripe.com/${STRIPE_PLATFORM_ACCOUNT}${mode}/connect/accounts/${accountId}/activity`;
}

/**
 * A field-level error.
 *
 * One shape for what used to be three: bare text-danger, text-danger mt-1, and the same
 * wrapped in <small>. role="alert" because these appeared silently.
 */
function FieldError({ children }) {
  return (
    <div className="text-danger small mt-2" role="alert">
      {children}
    </div>
  );
}

function YesNo({ value }) {
  return (
    <span className={value ? "text-success" : "text-danger"}>
      {value ? "Yes" : "No"}
    </span>
  );
}

function SaveButton({ onClick, disabled, isSaving }) {
  return (
    <Button
      variant="primary"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="me-2"
    >
      {isSaving ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            aria-hidden="true"
          ></span>
          Saving...
        </>
      ) : (
        "Save"
      )}
    </Button>
  );
}

function CancelButton({ onClick, disabled }) {
  return (
    <Button
      variant="outline-secondary"
      size="sm"
      onClick={onClick}
      disabled={disabled}
    >
      Cancel
    </Button>
  );
}

/**
 * A read-only value with its Edit control.
 *
 * The Edit button used to sit in the label cell, and all three were announced as just
 * "Edit". It belongs with the value it acts on, and its name has to say which field --
 * the row action button on the main table already does this.
 */
function ValueWithEdit({ value, canEdit, onEdit, editLabel }) {
  const isEmpty = value === null || value === undefined || value === "";

  return (
    <span className="d-inline-flex align-items-center">
      {isEmpty ? <span className="text-secondary">Not set</span> : value}
      {canEdit && (
        <Button
          variant="link"
          size="sm"
          className="ms-2 p-0 align-baseline"
          aria-label={`Edit ${editLabel}`}
          onClick={onEdit}
        >
          <i className="bi bi-pencil" aria-hidden="true" /> Edit
        </Button>
      )}
    </span>
  );
}

/**
 * One label/value pair in the details grid.
 *
 * A <dl> rather than the table rows this replaces: these are label/value pairs, not tabular
 * data, and those tables had no headers, so a screen reader announced them as data tables
 * with nothing to orient by. dt/dd are returned as a fragment to stay direct children of
 * the <dl>.
 *
 * `empty` exists because a missing value used to render six different ways across this one
 * modal: "N/A", blank, "No", "Active", "No metadata available", and a sentence. It applies
 * to nullish and empty string only, never to false, or "Valid: No" would come out blank.
 */
function DetailRow({ label, children, empty = "Not set" }) {
  const isEmpty =
    children === null || children === undefined || children === "";

  return (
    <>
      <dt className="col-sm-5 fw-semibold">{label}</dt>
      <dd className="col-sm-7 mb-2">
        {isEmpty ? <span className="text-secondary">{empty}</span> : children}
      </dd>
    </>
  );
}

/**
 * A titled group of DetailRows.
 *
 * h3 because the dialog title is the h2 of this subtree. The modal used to jump h4 to h5
 * with no h2 or h3 anywhere in the app.
 */
function DetailSection({ title, children }) {
  return (
    <section className="mb-4">
      <h3 className="text-uppercase text-secondary fw-semibold small mb-3">
        {title}
      </h3>
      <dl className="row mb-0">{children}</dl>
    </section>
  );
}

/**
 * A raw JSON blob from Stripe.
 *
 * Bounded and focusable: these used to be unbounded <pre> blocks that stretched the dialog,
 * and a keyboard user could not scroll them.
 */
function JsonBlock({ value }) {
  return (
    <pre className="mb-0 small bg-light rounded p-2 detail-json" tabIndex={0}>
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/**
 * A Unix-seconds timestamp, or nothing.
 *
 * Follows ManageInvites: guarded, because these were rendered straight into new Date(x *
 * 1000) and a non-numeric value produced the string "Invalid Date".
 */
function SchemeTimestamp({ seconds }) {
  if (!Number.isFinite(seconds)) return null;
  const at = new Date(seconds * 1000);
  if (Number.isNaN(at.getTime())) return null;
  return <span title={at.toLocaleString()}>{at.toLocaleDateString()}</span>;
}

function MerchantsTable({
  data,
  onMerchantUpdate,
  onMerchantDelete,
  configData,
}) {
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingStatementDescription, setEditingStatementDescription] =
    useState(false);
  const [editingAccountStatus, setEditingAccountStatus] = useState(false);
  const [accountStatus, setAccountStatus] = useState(AccountStatus.Active);
  const [statementDescription, setStatementDescription] = useState("");
  const [statementDescriptionError, setStatementDescriptionError] =
    useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState({
    statementDescription: null,
    accountStatus: null,
    icPlusPlus: null,
    startOfDay: null,
  });
  // Delete state. This is the app's first destructive action, so the confirm step is
  // deliberately heavier than the inline Save/Cancel used everywhere else.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  //  What the operator has to type to arm the delete. The Stripe account id is the more
  //  meaningful thing to confirm: it is what identifies the account that is about to be
  //  destroyed, and merchant names are not unique.
  //
  //  Falls back to the name when there is no Stripe account, because a merchant whose
  //  onboarding never completed has none, and that is precisely the row this feature exists
  //  to clean up. Requiring an id that does not exist would make it undeletable.
  const deleteConfirmTarget =
    selectedMerchant?.paymentProcessorAccountId || selectedMerchant?.name || "";
  const deleteConfirmIsAccountId =
    !!selectedMerchant?.paymentProcessorAccountId;
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deletability, setDeletability] = useState(null);
  const [isCheckingDeletability, setIsCheckingDeletability] = useState(false);

  const [isLoadingStripeData, setIsLoadingStripeData] = useState(false);
  const [stripeAccountData, setStripeAccountData] = useState(null);
  const [stripeError, setStripeError] = useState(null);

  // IC++ (Network Cost Passthrough) state
  const [editingIcPlusPlus, setEditingIcPlusPlus] = useState(false);
  const [icPlusPlusEnabled, setIcPlusPlusEnabled] = useState(false);
  const [icPlusPlusData, setIcPlusPlusData] = useState(null);
  const [isLoadingIcPlusPlus, setIsLoadingIcPlusPlus] = useState(false);
  const [icPlusPlusError, setIcPlusPlusError] = useState(null);

  // Payout start of day state. startOfDay is what Stripe has; startOfDayDraft is the editor.
  const [editingStartOfDay, setEditingStartOfDay] = useState(false);
  const [startOfDay, setStartOfDay] = useState(null);
  const [startOfDayDraft, setStartOfDayDraft] = useState(null);
  const [isLoadingStartOfDay, setIsLoadingStartOfDay] = useState(false);
  const [startOfDayError, setStartOfDayError] = useState(null);

  //  Cosmetic only. The API policy is what actually stops a non-admin editing or deleting
  //  a merchant; this just keeps the controls out of the way of people who cannot use them.
  const { isTowpayAdmin } = useAuth();

  // Function to fetch Stripe account data
  const fetchStripeAccountData = async (merchant) => {
    if (
      !merchant ||
      !merchant.paymentProcessorAccountId ||
      !configData ||
      !configData.merchantConfigurationBaseUri
    ) {
      return;
    }

    setIsLoadingStripeData(true);
    setStripeError(null);

    try {
      const accessToken = requireToken();

      const response = await getFromMCAPI(
        accessToken,
        `${configData.merchantConfigurationBaseUri}/api/merchants/stripe/${merchant.paymentProcessorAccountId}`,
      );

      setAccountStatus(response.metadata?.account_status || "");
      console.log("account status", response.metadata?.account_status);
      setStripeAccountData(response);
    } catch (error) {
      console.error("Failed to fetch Stripe account data:", error);
      setStripeError(
        "Failed to load Stripe account details. Please try again.",
      );
      setStripeAccountData(null);
    } finally {
      setIsLoadingStripeData(false);
    }
  };

  // Function to fetch IC++ status
  const fetchIcPlusPlusStatus = async (merchant) => {
    if (
      !merchant ||
      !merchant.merchantId ||
      !configData ||
      !configData.merchantConfigurationBaseUri
    ) {
      return;
    }

    setIsLoadingIcPlusPlus(true);
    setIcPlusPlusError(null);

    try {
      const accessToken = requireToken();

      const response = await getIcPlusPlusStatus(
        accessToken,
        configData.merchantConfigurationBaseUri,
        merchant.merchantId,
      );

      setIcPlusPlusEnabled(response.icPlusPlusEnabled || false);
      setIcPlusPlusData(response);
    } catch (error) {
      console.error("Failed to fetch IC++ status:", error);
      setIcPlusPlusError("Failed to load IC++ status. Please try again.");
      setIcPlusPlusData(null);
    } finally {
      setIsLoadingIcPlusPlus(false);
    }
  };

  // Function to fetch the payout start of day. Lives in Stripe's balance settings, so a
  // merchant that never finished onboarding has nothing to show.
  const fetchStartOfDay = async (merchant) => {
    if (
      !merchant ||
      !merchant.merchantId ||
      !merchant.paymentProcessorAccountId ||
      !configData ||
      !configData.merchantConfigurationBaseUri
    ) {
      return;
    }

    setIsLoadingStartOfDay(true);
    setStartOfDayError(null);

    try {
      const accessToken = requireToken();

      const response = await getStartOfDay(
        accessToken,
        configData.merchantConfigurationBaseUri,
        merchant.merchantId,
      );

      setStartOfDay(response);
    } catch (error) {
      console.error("Failed to fetch start of day:", error);
      setStartOfDayError("Failed to load start of day. Please try again.");
      setStartOfDay(null);
    } finally {
      setIsLoadingStartOfDay(false);
    }
  };

  // Read-only dry run of the API's pre-delete guard. Failures here are non-fatal: the
  // real delete runs the same guard server-side, so the worst case is that the button
  // stays enabled and the 409 arrives a step later.
  const fetchDeletability = async (merchant) => {
    if (!merchant || !configData?.merchantConfigurationBaseUri) return;

    setIsCheckingDeletability(true);
    setDeletability(null);

    try {
      const accessToken = requireToken();

      const response = await getMerchantDeletability(
        accessToken,
        configData.merchantConfigurationBaseUri,
        merchant.merchantId,
      );

      setDeletability(response);
    } catch (error) {
      console.error("Error checking merchant deletability:", error);
    } finally {
      setIsCheckingDeletability(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await onMerchantDelete(selectedMerchant);

      // The row is gone from the table now, so there is nothing left to show.
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");
      handleCloseModal();
    } catch (error) {
      // Show what the API actually said. A 409 body names the balance or the last
      // charge, which is the whole reason the operator is here.
      setDeleteError(error.message || "Failed to delete the merchant.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShowDetails = useCallback((merchant) => {
    setSelectedMerchant(merchant);
    setStatementDescription(merchant.payouts?.statement_descriptor || "");
    setEditingStatementDescription(false);
    setEditingAccountStatus(false);
    setEditingIcPlusPlus(false);
    setEditingStartOfDay(false);
    setSaveError({
      statementDescription: null,
      accountStatus: null,
      icPlusPlus: null,
      startOfDay: null,
    });
    setStatementDescriptionError("");
    setIcPlusPlusError(null);
    setStartOfDay(null);
    setStartOfDayError(null);
    setShowModal(true);

    setDeleteError(null);
    setDeletability(null);

    // Fetch Stripe account data when modal opens
    fetchStripeAccountData(merchant);
    // Fetch IC++ status when modal opens
    fetchIcPlusPlusStatus(merchant);
    // Fetch payout start of day when modal opens
    fetchStartOfDay(merchant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask the API whether this merchant could be deleted, so the button can be disabled
  // with a reason rather than failing after someone has typed the merchant name.
  // Kept out of handleShowDetails because isTowpayAdmin resolves asynchronously and
  // that callback has empty deps, so it would read a stale value.
  useEffect(() => {
    if (!showModal || !selectedMerchant || !isTowpayAdmin) return;

    fetchDeletability(selectedMerchant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, selectedMerchant, isTowpayAdmin]);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowDeleteConfirm(false);
    setDeleteConfirmText("");
    setDeleteError(null);
    setDeletability(null);
    setSaveError({
      statementDescription: null,
      accountStatus: null,
      icPlusPlus: null,
      startOfDay: null,
    });
    setStripeAccountData(null);
    setStripeError(null);
    setAccountStatus(AccountStatus.Active);
    setEditingAccountStatus(false);
    setEditingIcPlusPlus(false);
    setIcPlusPlusEnabled(false);
    setIcPlusPlusData(null);
    setIcPlusPlusError(null);
    setEditingStartOfDay(false);
    setStartOfDay(null);
    setStartOfDayDraft(null);
    setStartOfDayError(null);
  };

  const toggleEditMode = () => {
    setEditingStatementDescription(!editingStatementDescription);
  };

  const toggleEditAccountStatus = () => {
    setEditingAccountStatus(!editingAccountStatus);
  };

  const toggleEditIcPlusPlus = () => {
    setEditingIcPlusPlus(!editingIcPlusPlus);
  };

  const toggleEditStartOfDay = () => {
    if (!editingStartOfDay) {
      setStartOfDayDraft({
        timezone: startOfDay?.timezone || DEFAULT_TIMEZONE,
        hour: startOfDay?.hour ?? 0,
        minutes: startOfDay?.minutes ?? 0,
      });
    }
    setEditingStartOfDay(!editingStartOfDay);
  };

  // Keep the picked time if the new timezone allows it. Otherwise fall back to midnight,
  // which every timezone allows (UTC allows nothing else, and Hawaii stops at 05:00).
  const handleStartOfDayTimezoneChange = (timezone) => {
    setStartOfDayDraft((draft) =>
      isAllowedTime(timezone, draft.hour, draft.minutes)
        ? { ...draft, timezone }
        : { timezone, hour: 0, minutes: 0 },
    );
  };

  // Validate statement descriptor according to Stripe requirements
  const validateStatementDescriptor = (descriptor) => {
    if (!descriptor || descriptor.trim() === "") {
      return "Statement descriptor cannot be empty.";
    }

    // Must be between 5 and 22 characters
    if (descriptor.length < 5) {
      return "Statement descriptor must be at least 5 characters long.";
    }

    if (descriptor.length > 22) {
      return "Statement descriptor cannot exceed 22 characters.";
    }

    // Must contain at least one Latin letter (a-z, A-Z)
    const hasLatinLetter = /[a-zA-Z]/.test(descriptor);
    if (!hasLatinLetter) {
      return "Statement descriptor must contain at least one letter (a-z or A-Z).";
    }

    // Cannot contain special characters: < > \ ' " *
    const invalidChars = ["<", ">", "\\", "'", '"', "*"];
    for (const char of invalidChars) {
      if (descriptor.includes(char)) {
        return `Statement descriptor cannot contain the character '${char}'.`;
      }
    }

    return null; // Valid
  };

  const handleSaveStatementDescription = async () => {
    if (!selectedMerchant) return;

    // Validate the statement descriptor before saving
    const validationError = validateStatementDescriptor(statementDescription);
    if (validationError) {
      setStatementDescriptionError(validationError);
      return;
    }

    setStatementDescriptionError("");
    setIsSaving(true);
    setSaveError({
      statementDescription: null,
      accountStatus: null,
      icPlusPlus: null,
      startOfDay: null,
    });

    try {
      const updatedMerchant = {
        ...selectedMerchant,
        payouts: {
          ...selectedMerchant.payouts,
          statement_descriptor: statementDescription,
        },
      };

      // Call the update function passed from the parent component
      await onMerchantUpdate(updatedMerchant);

      // Update the local state with the new statement descriptor
      setSelectedMerchant(updatedMerchant);
      setEditingStatementDescription(false);

      // Reload Stripe account data after successful save to ensure data is up-to-date
      if (updatedMerchant.paymentProcessorAccountId) {
        fetchStripeAccountData(updatedMerchant);
      }
    } catch (error) {
      console.error("Failed to save payout statement descriptor:", error);
      setSaveError({
        statementDescription: "Failed to save changes. Please try again.",
        accountStatus: null,
        icPlusPlus: null,
        startOfDay: null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAccountStatus = async () => {
    if (!selectedMerchant) return;

    setIsSaving(true);
    setSaveError({
      statementDescription: null,
      accountStatus: null,
      icPlusPlus: null,
      startOfDay: null,
    });

    try {
      const updatedMerchant = {
        ...selectedMerchant,
        metadata: {
          ...selectedMerchant.metadata,
          account_status: accountStatus,
        },
      };

      // Call the update function passed from the parent component
      await onMerchantUpdate(updatedMerchant);

      // Update the local state with the new account status
      setSelectedMerchant(updatedMerchant);
      setEditingAccountStatus(false);

      // Reload Stripe account data after successful save to ensure data is up-to-date
      if (updatedMerchant.paymentProcessorAccountId) {
        fetchStripeAccountData(updatedMerchant);
      }
    } catch (error) {
      console.error("Failed to save account status:", error);
      setSaveError({
        statementDescription: null,
        accountStatus: "Failed to save changes. Please try again.",
        icPlusPlus: null,
        startOfDay: null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveIcPlusPlus = async () => {
    if (!selectedMerchant) return;

    setIsSaving(true);
    setSaveError({
      statementDescription: null,
      accountStatus: null,
      icPlusPlus: null,
      startOfDay: null,
    });

    try {
      const accessToken = requireToken();

      // Call the IC++ API to update the status
      const response = await updateIcPlusPlusStatus(
        accessToken,
        configData.merchantConfigurationBaseUri,
        selectedMerchant.merchantId,
        icPlusPlusEnabled,
      );

      // Update the IC++ data with the response
      setIcPlusPlusData(response);
      setEditingIcPlusPlus(false);

      // Refresh IC++ status after save
      fetchIcPlusPlusStatus(selectedMerchant);
    } catch (error) {
      console.error("Failed to save IC++ status:", error);
      setSaveError({
        statementDescription: null,
        accountStatus: null,
        icPlusPlus: "Failed to save IC++ status. Please try again.",
        startOfDay: null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveStartOfDay = async () => {
    if (!selectedMerchant || !startOfDayDraft) return;

    setIsSaving(true);
    setSaveError({
      statementDescription: null,
      accountStatus: null,
      icPlusPlus: null,
      startOfDay: null,
    });

    try {
      const accessToken = requireToken();

      const response = await updateStartOfDay(
        accessToken,
        configData.merchantConfigurationBaseUri,
        selectedMerchant.merchantId,
        startOfDayDraft,
      );

      setStartOfDay(response);
      setEditingStartOfDay(false);
    } catch (error) {
      console.error("Failed to save start of day:", error);
      // Show what the API said: when Stripe refuses a value, its message lists what it allows.
      setSaveError({
        statementDescription: null,
        accountStatus: null,
        icPlusPlus: null,
        startOfDay:
          error.message || "Failed to save start of day. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const merchants = useMemo(() => data, [data]);
  const columns = useMemo(
    () => [
      {
        header: "TowPay Merchant ID",
        accessor: "merchantId",
        Cell: ({ value }) => (
          <CopyableId value={value} label="TowPay merchant ID" />
        ),
        Filter: ColumnFilter,
      },
      {
        header: "Country",
        accessor: "country",
        Filter: ColumnFilter,
      },
      {
        header: "Stripe Account ID",
        accessor: "paymentProcessorAccountId",
        Filter: ColumnFilter,
      },
      {
        header: "Controller Email",
        accessor: "controllerEmail",
        Filter: ColumnFilter,
      },
      {
        header: "Actions",
        id: "actions",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => (
          //  "Details" rather than "Show Details": the label was wrapping onto two lines
          //  in a column this narrow, and the row it sits in already says what the details
          //  are about. text-nowrap keeps it on one line at any width. The aria-label
          //  carries the context the shorter label drops, so a screen reader hears
          //  something better than fifty buttons all called "Details".
          <Button
            variant="outline-primary"
            size="sm"
            className="text-nowrap"
            aria-label={`Details for ${
              row.original.name || row.original.paymentProcessorAccountId
            }`}
            onClick={() => handleShowDetails(row.original)}
          >
            Details
          </Button>
        ),
      },
    ],
    [handleShowDetails],
  );

  const defaultColumn = useMemo(
    () => ({
      // Default Filter UI
      Filter: ColumnFilter,
    }),
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data: merchants,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: "merchantId",
            desc: true,
          },
        ],
      },
    },
    useFilters,
    // useGlobalFilter,
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <>
      {/* <div className="mb-3">
        <GlobalFilter
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      </div> */}
      {/*  Matches Invite History. text-nowrap is inherited by every cell so rows stay one
           line tall, and table-responsive gives the table its own scrollbar rather than
           widening the page. Both are the safety net at narrow widths; the columns were
           trimmed so this rarely has to scroll. */}
      <div className="table-responsive">
        <table
          {...getTableProps()}
          className="table table-striped text-nowrap align-middle"
          aria-labelledby="tableLabel"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {/*  text-nowrap keeps the label and its sort arrow on one line.
                       Without it "Payment Processor Account ID" filled the cell and the
                       arrow wrapped underneath, making that header two lines tall and
                       knocking it out of line with the rest. The arrow is always
                       rendered, just invisible when the column is unsorted, so the
                       header does not jump width when you sort it. */}
                    <div className="text-nowrap">
                      {column.render("header")}
                      <i
                        className={`bi ms-1 ${
                          column.isSorted
                            ? column.isSortedDesc
                              ? "bi-arrow-down"
                              : "bi-arrow-up"
                            : "bi-arrow-up invisible"
                        }`}
                        aria-hidden="true"
                      />
                    </div>
                    {/*  stopPropagation because getSortByToggleProps() puts its onClick
                         on the <th>, and this filter sits inside that same <th>. Without
                         it, every click into a filter box bubbles up and re-sorts the
                         column out from under whoever was about to type in it. */}
                    <div
                      className="mt-2"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <tr>
                <td>No Merchants, currently.</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Merchant Details Modal */}
      {/*  aria-labelledby because react-bootstrap does not name the dialog for you, and
           neither modal in this file had an accessible name. */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        aria-labelledby="merchant-details-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="merchant-details-title" as="h2" className="h5">
            Merchant Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMerchant && (
            <div>
              <p className="fs-5 fw-semibold mb-4">
                {selectedMerchant.name || (
                  <span className="text-secondary">Unnamed merchant</span>
                )}
              </p>

              <DetailSection title="TowPay">
                <DetailRow label="TowPay Merchant ID">
                  {selectedMerchant.merchantId && (
                    <CopyableId
                      value={selectedMerchant.merchantId}
                      label="TowPay merchant ID"
                    />
                  )}
                </DetailRow>

                <DetailRow label="Country">
                  {selectedMerchant.country}
                </DetailRow>

                <DetailRow label="Controller email">
                  {selectedMerchant.controllerEmail}
                </DetailRow>

                <DetailRow label="Stripe Account ID">
                  {selectedMerchant.paymentProcessorAccountId && (
                    <span className="d-inline-flex align-items-center gap-2">
                      <CopyableId
                        value={selectedMerchant.paymentProcessorAccountId}
                        label="Stripe account ID"
                      />
                      <a
                        href={stripeAccountUrl(
                          selectedMerchant.paymentProcessorAccountId,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Stripe
                        <i
                          className="bi bi-box-arrow-up-right ms-1"
                          aria-hidden="true"
                        />
                      </a>
                    </span>
                  )}
                </DetailRow>

                <DetailRow label="Description">
                  {selectedMerchant.description}
                </DetailRow>

                <DetailRow label="Payout Statement Descriptor">
                  {editingStatementDescription ? (
                    <div>
                      <div className="d-flex align-items-center">
                        <input
                          id="statement-descriptor"
                          type="text"
                          className={`form-control me-2 ${
                            statementDescriptionError ? "is-invalid" : ""
                          }`}
                          aria-label="Payout statement descriptor"
                          value={statementDescription}
                          maxLength={22}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setStatementDescription(newValue);
                            // Validate in real-time as user types
                            const error = validateStatementDescriptor(newValue);
                            setStatementDescriptionError(error || "");
                          }}
                          placeholder="5-22 characters, must include a letter"
                        />
                        <SaveButton
                          onClick={handleSaveStatementDescription}
                          disabled={isSaving || !!statementDescriptionError}
                          isSaving={isSaving}
                        />
                        <CancelButton
                          onClick={toggleEditMode}
                          disabled={isSaving}
                        />
                      </div>
                      {statementDescriptionError && (
                        <FieldError>{statementDescriptionError}</FieldError>
                      )}
                      <div className="text-secondary mt-2 small">
                        {statementDescription.length}/22 characters
                      </div>
                    </div>
                  ) : (
                    <ValueWithEdit
                      value={selectedMerchant.payouts?.statement_descriptor}
                      canEdit={isTowpayAdmin}
                      onEdit={toggleEditMode}
                      editLabel="payout statement descriptor"
                    />
                  )}
                  {saveError.statementDescription && (
                    <FieldError>{saveError.statementDescription}</FieldError>
                  )}
                </DetailRow>

                <DetailRow label="Account Status">
                  {editingAccountStatus ? (
                    <div className="d-flex align-items-center">
                      <div className="me-2 account-status-field">
                        <AccountStatusDropdown
                          value={accountStatus}
                          onChange={(value) => setAccountStatus(value)}
                          label="Account status"
                          size="sm"
                        />
                      </div>
                      <SaveButton
                        onClick={handleSaveAccountStatus}
                        disabled={isSaving}
                        isSaving={isSaving}
                      />
                      <CancelButton
                        onClick={toggleEditAccountStatus}
                        disabled={isSaving}
                      />
                    </div>
                  ) : (
                    <ValueWithEdit
                      value={formatStatusLabel(accountStatus)}
                      canEdit={isTowpayAdmin}
                      onEdit={toggleEditAccountStatus}
                      editLabel="account status"
                    />
                  )}
                  {saveError.accountStatus && (
                    <FieldError>{saveError.accountStatus}</FieldError>
                  )}
                </DetailRow>

                <DetailRow label="IC++">
                  {isLoadingIcPlusPlus ? (
                    <span className="d-flex align-items-center text-secondary">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        aria-hidden="true"
                      ></span>
                      Loading IC++ status...
                    </span>
                  ) : icPlusPlusError ? (
                    <FieldError>{icPlusPlusError}</FieldError>
                  ) : editingIcPlusPlus ? (
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id="ic-plus-plus-switch"
                        label={icPlusPlusEnabled ? "Enabled" : "Disabled"}
                        checked={icPlusPlusEnabled}
                        onChange={(e) => setIcPlusPlusEnabled(e.target.checked)}
                        className="me-2"
                      />
                      <SaveButton
                        onClick={handleSaveIcPlusPlus}
                        disabled={isSaving}
                        isSaving={isSaving}
                      />
                      <CancelButton
                        onClick={toggleEditIcPlusPlus}
                        disabled={isSaving}
                      />
                    </div>
                  ) : (
                    <div>
                      <ValueWithEdit
                        value={
                          <span
                            className={
                              icPlusPlusEnabled
                                ? "text-success"
                                : "text-secondary"
                            }
                          >
                            {icPlusPlusEnabled ? "Enabled" : "Disabled"}
                          </span>
                        }
                        canEdit={isTowpayAdmin}
                        onEdit={toggleEditIcPlusPlus}
                        editLabel="IC++"
                      />
                      <div className="text-secondary small">
                        Network cost passthrough
                      </div>
                      {icPlusPlusEnabled && icPlusPlusData?.currentScheme && (
                        <div className="mt-2 p-2 bg-light rounded small">
                          <div>Scheme {icPlusPlusData.currentScheme.id}</div>
                          <div>
                            Started{" "}
                            <SchemeTimestamp
                              seconds={icPlusPlusData.currentScheme.startsAt}
                            />
                          </div>
                          {icPlusPlusData.currentScheme.endsAt && (
                            <div>
                              Ends{" "}
                              <SchemeTimestamp
                                seconds={icPlusPlusData.currentScheme.endsAt}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {icPlusPlusData?.nextScheme && (
                        <div className="mt-2 p-2 bg-warning bg-opacity-25 rounded small">
                          <div className="fw-semibold">Scheduled change</div>
                          <div>
                            <span
                              className={
                                icPlusPlusData.nextScheme.enabled
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {icPlusPlusData.nextScheme.enabled
                                ? "Will be enabled"
                                : "Will be disabled"}
                            </span>
                          </div>
                          <div>
                            Starts{" "}
                            <SchemeTimestamp
                              seconds={icPlusPlusData.nextScheme.startsAt}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {saveError.icPlusPlus && (
                    <FieldError>{saveError.icPlusPlus}</FieldError>
                  )}
                </DetailRow>

                <DetailRow label="Payout start of day">
                  {!selectedMerchant.paymentProcessorAccountId ? null : isLoadingStartOfDay ? (
                    <span className="d-flex align-items-center text-secondary">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        aria-hidden="true"
                      ></span>
                      Loading start of day...
                    </span>
                  ) : startOfDayError ? (
                    <FieldError>{startOfDayError}</FieldError>
                  ) : editingStartOfDay && startOfDayDraft ? (
                    <div>
                      <div className="d-flex align-items-center">
                        <Form.Select
                          size="sm"
                          className="me-2"
                          aria-label="Start of day timezone"
                          value={startOfDayDraft.timezone}
                          onChange={(e) =>
                            handleStartOfDayTimezoneChange(e.target.value)
                          }
                        >
                          {START_OF_DAY_TIMEZONES.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Select
                          size="sm"
                          className="me-2"
                          aria-label="Start of day time"
                          value={formatTime(
                            startOfDayDraft.hour,
                            startOfDayDraft.minutes,
                          )}
                          disabled={startOfDayDraft.timezone === DEFAULT_TIMEZONE}
                          onChange={(e) => {
                            const [hour, minutes] = e.target.value
                              .split(":")
                              .map(Number);
                            setStartOfDayDraft({ ...startOfDayDraft, hour, minutes });
                          }}
                        >
                          {timeOptionsFor(startOfDayDraft.timezone, startOfDay).map(
                            (t) => (
                              <option
                                key={formatTime(t.hour, t.minutes)}
                                value={formatTime(t.hour, t.minutes)}
                              >
                                {formatTime(t.hour, t.minutes)}
                              </option>
                            ),
                          )}
                        </Form.Select>
                        <SaveButton
                          onClick={handleSaveStartOfDay}
                          disabled={isSaving}
                          isSaving={isSaving}
                        />
                        <CancelButton
                          onClick={toggleEditStartOfDay}
                          disabled={isSaving}
                        />
                      </div>
                      <div className="text-secondary mt-2 small">
                        Takes effect at the next start of day. Balances already
                        earned keep their old payout schedule.
                      </div>
                    </div>
                  ) : (
                    <div>
                      <ValueWithEdit
                        value={formatStartOfDay(startOfDay)}
                        canEdit={isTowpayAdmin}
                        onEdit={toggleEditStartOfDay}
                        editLabel="payout start of day"
                      />
                      <div className="text-secondary small">
                        Automatic payouts group payments by this local day
                      </div>
                    </div>
                  )}
                  {saveError.startOfDay && (
                    <FieldError>{saveError.startOfDay}</FieldError>
                  )}
                </DetailRow>

                <DetailRow label="Valid">
                  {selectedMerchant.isValid ? "Yes" : "No"}
                </DetailRow>

                <DetailRow label="Partner ID">
                  {selectedMerchant.partnerId}
                </DetailRow>
              </DetailSection>

              <DetailSection title="Stripe">
                {isLoadingStripeData ? (
                  <div className="col-12 text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">
                        Loading Stripe account details...
                      </span>
                    </div>
                    <p className="mt-3 mb-0" aria-hidden="true">
                      Loading Stripe account details...
                    </p>
                  </div>
                ) : stripeError ? (
                  <div className="col-12">
                    <div className="alert alert-danger mb-0" role="alert">
                      {stripeError}
                    </div>
                  </div>
                ) : stripeAccountData ? (
                  <>
                    <DetailRow label="Charges enabled">
                      <YesNo value={stripeAccountData.charges_enabled} />
                    </DetailRow>
                    <DetailRow label="Payouts enabled">
                      <YesNo value={stripeAccountData.payouts_enabled} />
                    </DetailRow>
                    <DetailRow label="Metadata" empty="None">
                      {Object.keys(stripeAccountData.metadata || {}).length >
                        0 && <JsonBlock value={stripeAccountData.metadata} />}
                    </DetailRow>
                    <DetailRow label="Settings" empty="None">
                      {Object.keys(stripeAccountData.settings || {}).length >
                        0 && <JsonBlock value={stripeAccountData.settings} />}
                    </DetailRow>
                  </>
                ) : (
                  <p className="col-12 text-secondary mb-0">
                    {/*  Distinguished from a load failure: a merchant with no account has
                         nothing to show, which is a fact rather than a problem. */}
                    {selectedMerchant.paymentProcessorAccountId
                      ? "No Stripe account details available."
                      : "This merchant has no Stripe account yet."}
                  </p>
                )}
              </DetailSection>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isTowpayAdmin && (
            <div className="me-auto">
              <Button
                variant="danger"
                onClick={() => {
                  setDeleteConfirmText("");
                  setDeleteError(null);
                  setShowDeleteConfirm(true);
                }}
                disabled={
                  isCheckingDeletability ||
                  (deletability !== null && !deletability.safe_to_delete)
                }
              >
                {isCheckingDeletability ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                    Checking...
                  </>
                ) : (
                  "Delete Merchant"
                )}
              </Button>
              {deletability !== null && !deletability.safe_to_delete && (
                <div className="deletability-reason">
                  {/*  Falls back to a plain sentence rather than interpolating reason
                       unconditionally: with neither field set this used to render the
                       literal "Cannot be deleted (undefined)." */}
                  <FieldError>
                    {deletability.message ||
                      (deletability.reason
                        ? `Cannot be deleted (${deletability.reason}).`
                        : "This merchant cannot be deleted.")}
                  </FieldError>
                </div>
              )}
            </div>
          )}
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/*
        Typed-name confirm. This is the only irreversible action in the app: it destroys
        the TowPay merchant, its invites, AND the Stripe connected account.
      */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => !isDeleting && setShowDeleteConfirm(false)}
        backdrop="static"
      >
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Delete merchant permanently?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This permanently deletes the TowPay merchant{" "}
            <strong>{selectedMerchant?.name}</strong>, every invite that
            references it, and its Stripe connected account{" "}
            <strong>
              {selectedMerchant?.paymentProcessorAccountId || "(none)"}
            </strong>
            .
          </p>
          <p className="text-danger">
            <strong>None of this can be undone.</strong> The Stripe account
            cannot be restored, and any outstanding payment link or onboarding
            link for this merchant will stop working.
          </p>
          <Form.Group>
            <Form.Label>
              Type the{" "}
              {deleteConfirmIsAccountId ? "Stripe account ID" : "merchant name"}{" "}
              <strong>{deleteConfirmTarget}</strong> to confirm:
            </Form.Label>
            <Form.Control
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={isDeleting}
              autoComplete="off"
              spellCheck="false"
              aria-label={
                deleteConfirmIsAccountId
                  ? "Stripe account ID confirmation"
                  : "Merchant name confirmation"
              }
            />
          </Form.Group>
          {deleteError && <div className="text-danger mt-2">{deleteError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={
              isDeleting ||
              //  The empty check matters: without it a merchant with neither an account id
              //  nor a name would leave the target as "", which an untouched input matches,
              //  arming the button with nothing typed at all.
              !deleteConfirmTarget ||
              deleteConfirmText !== deleteConfirmTarget
            }
          >
            {isDeleting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              "Delete permanently"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function GetMerchantData({ configData }) {
  const [apiData, setApiData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define loadMerchants function outside of useEffect but inside component
  const loadMerchants = useCallback(async () => {
    if (!configData.merchantConfigurationBaseUri) return;

    try {
      setIsLoading(true);
      const accessToken = requireToken();

      const response = await getFromMCAPI(
        accessToken,
        configData.merchantConfigurationBaseUri + "/api/merchants",
      );

      //  An empty list and a failed call must not look the same to the user.
      setApiData(Array.isArray(response) ? response : []);
      setError(null);
    } catch (error) {
      //  requireToken is already navigating to the login screen; overwriting the page with
      //  "failed to load" for the frame before that lands is just noise.
      if (error instanceof SessionExpiredError) throw error;
      setError("Failed to load merchant data");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [configData.merchantConfigurationBaseUri]);

  const handleMerchantUpdate = useCallback(
    async (updatedMerchant) => {
      if (!configData.merchantConfigurationBaseUri) {
        throw new Error("Configuration not available");
      }

      try {
        const accessToken = requireToken();

        // Update the merchant via API
        await updateMerchantInMCAPI(
          accessToken,
          `${configData.merchantConfigurationBaseUri}/api/merchants/${updatedMerchant.merchantId}`,
          updatedMerchant,
        );

        // Update local state with the updated merchant
        setApiData((prevData) =>
          prevData.map((merchant) =>
            merchant.merchantId === updatedMerchant.merchantId
              ? updatedMerchant
              : merchant,
          ),
        );

        return updatedMerchant;
      } catch (error) {
        console.error("Error updating merchant:", error);
        throw error;
      }
    },
    [configData.merchantConfigurationBaseUri],
  );

  const handleMerchantDelete = useCallback(
    async (merchantToDelete) => {
      if (!configData.merchantConfigurationBaseUri) {
        throw new Error("Configuration not available");
      }

      await deleteMerchantInMCAPI(
        requireToken(),
        configData.merchantConfigurationBaseUri,
        merchantToDelete.merchantId,
      );

      // Drop it from the table rather than refetching; the row is gone server-side.
      setApiData((prevData) =>
        prevData.filter(
          (merchant) => merchant.merchantId !== merchantToDelete.merchantId,
        ),
      );
    },
    [configData.merchantConfigurationBaseUri],
  );

  // Use useEffect to load merchants when component mounts or dependencies change
  useEffect(() => {
    //  The MSAL "is an interaction already in flight" gate is gone with MSAL: the token is
    //  read straight out of sessionStorage, so there is nothing to wait for.
    if (!apiData && configData.merchantConfigurationBaseUri) {
      loadMerchants();
    }
  }, [configData.merchantConfigurationBaseUri, loadMerchants, apiData]);

  // Render the appropriate component based on state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading merchants...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">{error}</p>;
  }

  if (!apiData) {
    return <p>No merchant data available.</p>;
  }

  if (apiData.length === 0) {
    return <p>No merchants found.</p>;
  }

  return (
    <MerchantsTable
      data={apiData}
      onMerchantUpdate={handleMerchantUpdate}
      onMerchantDelete={handleMerchantDelete}
      configData={configData}
    />
  );
}

export class Merchants extends Component {
  static displayName = Merchants.name;

  constructor(props) {
    super(props);

    this.state = {
      merchants: [],
      loading: true,
      envConfigs: {},
    };
  }

  async componentDidMount() {
    const envConfigs = await configs();
    this.setState({ envConfigs });
  }

  render() {
    return (
      <div>
        <PageHeader
          title="Manage Merchants"
          titleId="tableLabel"
          lead="Merchants onboarded to TowPay, their account status and their Stripe connected account."
        />
        <GetMerchantData configData={this.state.envConfigs} />
      </div>
    );
  }
}
